import React from "react";
import { useNavigate } from "react-router-dom";
import { Group, Loader, Text } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconSitemap, IconAlertTriangle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useMappingJob } from "@hooks/extraction/useMappingJob";
import { MappingViewStatus } from "@models/mapping";

interface OpenMappingControlProps {
  processId: string;
  processItemId: string;
  enabled?: boolean;
}

/**
 * Sits to the left of the Export button in the extraction-result header.
 *
 * During the async-create gap (extraction done, mapping job not yet created)
 * it shows a "starting…" hint; while the job runs it shows a progress label;
 * on failure an error label; once `Completed` ("ready for view") it swaps to
 * an "Open mapping" button that deep-links to the job's result editor inside
 * the mapping module of the shell.
 *
 * Renders nothing once we've stopped expecting a job (no job + not waiting).
 */
const OpenMappingControl: React.FC<OpenMappingControlProps> = ({
  processId,
  processItemId,
  enabled = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { job, waiting } = useMappingJob(processId, processItemId, enabled);

  if (!job) {
    // Async-create gap: extraction finished, the mapping job is being
    // dispatched/created. Show a hint while we still expect it; render
    // nothing once the start-window has elapsed with no job.
    if (!waiting) {
      return null;
    }
    return (
      <Group gap={6} wrap="nowrap">
        <Loader size={12} />
        <Text size="xs" c="dimmed">
          {t("extraction.mappingStatus.starting")}
        </Text>
      </Group>
    );
  }

  if (job.status === MappingViewStatus.Completed) {
    // serviceId is the `${kind}#${pluginId}` slug the mapping module routes on
    // (see mapping-frontend buildMappingServiceViewModel / parseServiceId).
    const serviceId = `${job.service ?? ""}#${job.pluginId ?? ""}`;
    const target = `/mapping/services/${encodeURIComponent(serviceId)}/jobs/${encodeURIComponent(job.jobId)}`;
    const canOpen = Boolean(job.jobId && job.service && job.pluginId);

    return (
      <Button
        size="xs"
        variant="filled"
        color="brand"
        leftSection={<IconSitemap size={16} />}
        disabled={!canOpen}
        onClick={() => navigate(target)}
        className="open-mapping-button"
      >
        {t("extraction.mappingStatus.openMapping")}
      </Button>
    );
  }

  if (job.status === MappingViewStatus.Failed) {
    return (
      <Group gap={4} wrap="nowrap" c="red">
        <IconAlertTriangle size={14} />
        <Text size="xs">{t("extraction.mappingStatus.failed")}</Text>
      </Group>
    );
  }

  // Running (AwaitingUpload / Parsing / Running).
  const hasTotal = job.rowsTotal > 0;
  const label = hasTotal
    ? t("extraction.mappingStatus.rows", {
        done: job.rowsDone,
        total: job.rowsTotal,
      })
    : t("extraction.mappingStatus.preparing");

  return (
    <Group gap={6} wrap="nowrap">
      <Loader size={12} />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
};

export default OpenMappingControl;
